/**
 * A URL that is safe to put behind an href, or undefined.
 *
 * Only absolute http(s) URLs survive. Everything reaching this helper today
 * comes from third-party content - calendar invites composed by whoever booked
 * the meeting - so a `javascript:` or `data:` value would otherwise land in a
 * link we are inviting a rep to click. A protocol-relative or bare-host value
 * is rejected too: rendering it means guessing a scheme, and guessing is how
 * someone ends up on a look-alike host.
 *
 * Undefined is an ordinary result, not a failure. Callers are expected to have
 * a non-link fallback.
 */
export const getSafeHttpUrl = (
  raw: string | null | undefined
): string | undefined => {
  const trimmed = raw?.trim();

  if (!trimmed) {
    return undefined;
  }

  try {
    const { protocol } = new URL(trimmed);

    if (protocol !== "http:" && protocol !== "https:") {
      return undefined;
    }

    return trimmed;
  } catch {
    // Not parseable as an absolute URL.
    return undefined;
  }
};

/**
 * Get the main domain from a given URL
 * @param url
 * @returns
 *
 * Example usage:
 * const url1 = 'https://www.example.com';
 * const url2 = 'http://blog.example.com';
 * const url3 = 'https://sub.domain.example.co.uk';
 * const url4 = 'example.com';
 *
 * console.log(getMainDomain(url1)); // Output: example.com
 * console.log(getMainDomain(url2)); // Output: example.com
 * console.log(getMainDomain(url3)); // Output: example.co.uk
 * console.log(getMainDomain(url4)); // Output: example.com
 */
export function getMainDomain(url: string) {
  let domain;

  try {
    const urlObj = new URL(url);
    domain = urlObj.hostname;
  } catch {
    return "";
  }

  const parts = domain.split(".");
  if (parts.length >= 2) {
    domain = parts.slice(-2).join(".");
  }
  return domain;
}
